import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import {
  Button,
  Box,
  FormLabel,
  Image,
  Input,
  Text,
  Flex,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import GoogleIcon from "common/assets/img/googleIcon.png";
import OutlookIcon from "common/assets/img/outlookIcon.png";
import { constant } from "common/constant";
import CustomChipInput from "../CustomChipInput/CustomChipInput";
import { postApi } from "common/services/api";
import { toast } from "react-toastify";
import axios from "axios";
import { createWorkspaceOptions } from "common/constant";
import DummyUserImage from "common/assets/img/dummyUserImg.png";
import CameraIcon from "common/assets/img/CameraIcon.png";
import EditIcon from "common/assets/img/editIcon.png";
import { useDispatch } from "react-redux";
import { setUser } from "common/redux/reducers/localSlice";
import CheckIconCircle from "common/assets/img/checkIconCircle.png";
import MicrosoftIcon from "common/assets/img/microsoftIcon.png";
import { validatePassword } from "common/helpers/validation";

const WorkspaceModal = ({ onCloseWorkspace, isOpenWorkspace }) => {
  const [selectedOption, setSelectedOption] = useState(1);
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [logo, setLogo] = useState(null);
  const [isStepCompleted, setIsStepCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStep2, setIsStep2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [passwordValidationMessage, setPasswordValidationMessage] =
    useState("");
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const [isOutlookUser, setIsOutlookUser] = useState(false);
  const [isOutlookLoading, setIsOutlookLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { instance } = useMsal();

  const isStep1Valid =
    companyName.trim() !== "" &&
    firstName.trim() !== "" &&
    lastName.trim() !== "";
  const isStep2Valid =
    email.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const [validationMessages, setValidationMessages] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const step = searchParams.get("step");
  const token = searchParams.get("token");

  useEffect(() => {
    if (step === "1") {
      setSelectedOption(2);
      setIsStepCompleted(true);
      setIsStep2(true);

      // Check if this is a Google signup flow
      const googleProfile = localStorage.getItem("googleProfile");
      if (googleProfile) {
        const profile = JSON.parse(googleProfile);
        setFirstName(profile.firstName || "");
        setLastName(profile.lastName || "");
        setEmail(profile.email || "");
        setLogo(profile.picture || "");
        setIsGoogleUser(true);
      }

      // Check if this is an Outlook signup flow
      const outlookProfile = localStorage.getItem("outlookProfile");
      if (outlookProfile) {
        const profile = JSON.parse(outlookProfile);
        setFirstName(profile.firstName || "");
        setLastName(profile.lastName || "");
        setEmail(profile.email || "");
        setLogo(profile.picture || "");
        setIsOutlookUser(true);
      }
    } else if (step === "2" && token) {
      setLinkSent(true);
      setSelectedOption(2);
      setIsStep2(true);

      // Check if this is a Google user for password step
      const googleProfile = localStorage.getItem("googleProfile");
      if (googleProfile) {
        setIsGoogleUser(true);
      }

      // Check if this is an Outlook user for password step
      const outlookProfile = localStorage.getItem("outlookProfile");
      if (outlookProfile) {
        setIsOutlookUser(true);
      }
    }
  }, [searchParams]);

  // Google Login Handler
  const handleGoogleAuth = async (credentialResponse) => {
    try {
      setIsLoading(true);
      const idToken = credentialResponse.access_token;

      const response = await postApi(
        "api/user/google-auth",
        { token: idToken, isLogin: false },
        true
      );

      if (response?.data) {
        const {
          success,
          isNewUser,
          googleToken,
          step,
          user,
          tenant,
          googleProfile,
        } = response.data;

        if (isNewUser) {
          // New user - store session token and redirect to complete signup
          localStorage.setItem("signupToken", googleToken);

          // Store Google profile
          if (googleProfile) {
            localStorage.setItem(
              "googleProfile",
              JSON.stringify(googleProfile)
            );
            // Pre-fill the form
            setFirstName(googleProfile.firstName || "");
            setLastName(googleProfile.lastName || "");
            setEmail(googleProfile.email || "");
            setLogo(googleProfile.picture || "");
          }

          // Update URL params and move to step 2
          setSearchParams({ step: "1", token: googleToken });
          setSelectedOption(2);
          setIsStepCompleted(true);
          setIsStep2(true);
          setIsGoogleUser(true);

          toast.success(
            "Google authentication successful! Please complete your signup."
          );
        } else {
          // Existing user - normal login flow
          localStorage.setItem("token", googleToken);
          localStorage.setItem("user", JSON.stringify(user));
          dispatch(setUser(user));

          // Navigate based on user role
          if (user.role === "SUPER_ADMIN") {
            navigate("/superAdmin");
          } else {
            navigate("/crm/default");
          }
        }
      }
    } catch (error) {
      console.error("Google Auth Error:", error);
      toast.error(
        error?.response?.data?.message ||
          "Google authentication failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Outlook Login Handler
  const handleOutlookAuth = async () => {
    try {
      setIsOutlookLoading(true);

      const response = await instance.loginPopup(loginRequest);

      if (response && response.accessToken) {
        // Send the token to your backend
        const backendResponse = await postApi(
          "api/user/outlook-auth",
          {
            token: response.accessToken,
            idToken: response.idToken,
            isLogin: false,
          },
          true
        );

        if (backendResponse?.data) {
          const {
            success,
            isNewUser,
            outlookToken,
            step,
            user,
            tenant,
            microsoftProfile,
          } = backendResponse.data;

          if (isNewUser) {
            // New user - store session token and redirect to complete signup
            localStorage.setItem("signupToken", outlookToken);

            // Store Outlook profile
            if (microsoftProfile) {
              localStorage.setItem(
                "outlookProfile",
                JSON.stringify(microsoftProfile)
              );
              // Pre-fill the form
              setFirstName(microsoftProfile.firstName || "");
              setLastName(microsoftProfile.lastName || "");
              setEmail(microsoftProfile.email || "");
              setLogo(microsoftProfile.picture || "");
            }

            // Update URL params and move to step 2
            setSearchParams({ step: "1", token: outlookToken });
            setSelectedOption(2);
            setIsStepCompleted(true);
            setIsStep2(true);
            setIsOutlookUser(true);

            toast.success(
              "Microsoft authentication successful! Please complete your signup."
            );
          } else {
            localStorage.setItem("token", outlookToken);
            localStorage.setItem("user", JSON.stringify(user));
            dispatch(setUser(user));
            if (user.role === "SUPER_ADMIN") {
              navigate("/superAdmin");
            } else {
              navigate("/crm/default");
            }
          }
        }
      }
    } catch (error) {
      console.error("Outlook Auth Error:", error);

      // Handle specific error cases
      if (error.errorCode === "user_cancelled") {
        toast.info("Login cancelled");
      } else if (error.errorCode === "consent_required") {
        toast.error("Please grant the necessary permissions to continue");
      } else {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Microsoft authentication failed. Please try again."
        );
      }
    } finally {
      setIsOutlookLoading(false);
    }
  };

  // Initialize Google Login
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleAuth,
    onError: (error) => {
      console.error("Google Login Failed:", error);
      toast.error("Google login failed. Please try again.");
    },
  });

  const handlePasswordValidation = (password) => {
    const validationResult = validatePassword(password);
    setPasswordValidationMessage(validationResult.message);
    return validationResult.isValid;
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    handlePasswordValidation(newPassword);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsSubmitDisabled(false);
  };

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save organization details
  const handleSaveOrganization = async () => {
    const messages = {};
    if (!companyName.trim()) messages.companyName = "Company Name is required.";
    if (!firstName.trim()) messages.firstName = "First Name is required.";
    if (!lastName.trim()) messages.lastName = "Last Name is required.";

    setValidationMessages(messages);

    if (Object.keys(messages).length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    // Use the signup token for Google/Outlook users
    const authToken =
      isGoogleUser || isOutlookUser
        ? localStorage.getItem("signupToken")
        : searchParams.get("token");
    const data = {
      companyName,
      firstName,
      lastName,
      phoneNumber: "",
      profilePhoto: logo,
    };

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${constant.baseUrl}api/tenant/completeCompany`,
        data,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response?.data?.success) {
        toast.success(
          response?.data?.message || "Company details saved successfully"
        );
        if (isGoogleUser || isOutlookUser) {
          const { token: newToken, user } = response?.data;

          // Clear signup data
          localStorage.removeItem("signupToken");
          localStorage.removeItem("googleProfile");
          localStorage.removeItem("outlookProfile");

          // Set auth data
          localStorage.setItem("token", newToken);
          localStorage.setItem("user", JSON.stringify(user));
          dispatch(setUser(user));

          // Navigate based on role
          if (user.role === "SUPER_ADMIN") {
            navigate("/superAdmin");
          } else {
            navigate("/crm/default");
          }
        } else {
          if (response?.data?.token) {
            localStorage.setItem("signupToken", response.data.token);
          }
          setLinkSent(true);
        }
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "An unexpected error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
    setIsStepCompleted(true);
    setSelectedOption(2);
  };

  // Send signup link
  const handleSendSignupLink = async () => {
    const messages = {};
    if (!email.trim()) messages.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      messages.email = "Invalid email format.";

    setValidationMessages(messages);

    if (Object.keys(messages).length > 0) {
      toast.error("Please enter a valid email.");
      return;
    }

    const payload = { email };

    try {
      setIsLoading(true);
      const response = await postApi("api/tenant/requestVerification", payload);
      toast.success(response?.data?.message || "Signup link sent successfully");
      setIsSubmitDisabled(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "An unexpected error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Submit password
  const handlePasswordSubmit = async (skipPassword = false) => {
    if (!skipPassword) {
      const messages = {};
      if (!password.trim()) messages.password = "Password is required.";

      setValidationMessages(messages);

      if (Object.keys(messages).length > 0) {
        toast.error("Password is required.");
        return;
      }
    }

    const authToken =
      isGoogleUser || isOutlookUser
        ? localStorage.getItem("signupToken")
        : searchParams.get("token");

    if (!authToken) {
      toast.error("Session expired. Please start over.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${constant.baseUrl}api/tenant/setPassword`,
        {
          password: skipPassword ? undefined : password,
          skipPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Signup completed successfully!");
        const { token: newToken, user } = response?.data;

        // Clear signup data
        localStorage.removeItem("signupToken");
        localStorage.removeItem("googleProfile");
        localStorage.removeItem("outlookProfile");

        // Set auth data
        localStorage.setItem("token", newToken);
        localStorage.setItem("user", JSON.stringify(user));
        dispatch(setUser(user));

        // Navigate based on role
        if (user.role === "SUPER_ADMIN") {
          navigate("/superAdmin");
        } else {
          navigate("/crm/default");
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // Render dynamic content based on the selected step
  const renderContent = () => {
    if (linkSent) {
      // Regular password form
      return (
        <Box>
          <Text mb={4}>{constant.setPassword}</Text>
          <Box position="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={handlePasswordChange}
              mb={1}
              pr="40px"
            />
            <Box
              position="absolute"
              top="50%"
              right="10px"
              transform="translateY(-50%)"
              cursor="pointer"
              zIndex="1"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <ViewIcon /> : <ViewOffIcon />}
            </Box>
          </Box>
          {passwordValidationMessage && (
            <Text color="red.500" fontSize="sm" mb={4}>
              {passwordValidationMessage}
            </Text>
          )}
          <Box display="flex" gap={3} mt={4}>
            <Button
              variant="brand"
              onClick={() => handlePasswordSubmit(false)}
              isLoading={isLoading}
              spinner={<Box className="spinner" />}
              flex={1}
            >
              {constant.saveContinue}
            </Button>
          </Box>
        </Box>
      );
    }

    switch (selectedOption) {
      case 1:
        return (
          <Box>
            <Box
              className="workspacesyncBtns"
              display="flex"
              justifyContent="space-between"
              mb={4}
              mt={8}
            >
              <Button
                variant="outlined"
                width="48%"
                onClick={() => googleLogin()}
                isLoading={isLoading}
                isDisabled={isLoading || isOutlookLoading}
              >
                <Image
                  src={GoogleIcon}
                  alt="Google Sync"
                  mr={2}
                  height="22px"
                  width="22px"
                />
                {constant.googleSync}
              </Button>
              <Button
                variant="outlined"
                width="48%"
                onClick={handleOutlookAuth}
                isLoading={isOutlookLoading}
                isDisabled={isLoading || isOutlookLoading}
              >
                <Image
                  src={MicrosoftIcon}
                  alt="Microsoft Sync"
                  mr={2}
                  height="22px"
                  width="22px"
                />
                {constant.microsoftSync}
              </Button>
            </Box>
            <Flex
              align="center"
              mt={6}
              mb={2}
              width={"100%"}
              className="orDividerFull"
            >
              <Box flex="1" height="1px" bg="gray.200" />
              <Text
                px={4}
                fontSize={"14px"}
                color="gray.800"
                whiteSpace="nowrap"
              >
                Or
              </Text>
              <Box flex="1" height="1px" bg="gray.200" />
            </Flex>
            <Box mb={4} mt={6} className="titleBox">
              <Text className="workspaceStepLabel">{constant.signUpText}</Text>
              <Text className="workspaceStepDesc">
                {constant.nameOfOrganization}
              </Text>
            </Box>

            <Box className="workspaceInputbox">
              <FormLabel className="workspaceInputLabel">
                {constant.enterEmail}{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Input
                placeholder={constant.enterEmail}
                value={email}
                onChange={handleEmailChange}
                mb={1}
              />
              {validationMessages.email && (
                <Text color="red.500" fontSize="sm" mb={4}>
                  {validationMessages.email}
                </Text>
              )}
            </Box>

            <Button
              variant={"brand"}
              onClick={handleSendSignupLink}
              isDisabled={isSubmitDisabled}
              isLoading={isLoading}
              spinner={<Box className="spinner" />}
              w={"100%"}
              mt={"6px"}
            >
              {constant.saveContinue}
            </Button>
          </Box>
        );
      case 2:
        return (
          <Box>
            {!linkSent ? (
              <>
                <Box
                  mb={5}
                  className="uploadLogoContainer"
                  display="flex"
                  alignItems="center"
                >
                  <Box
                    position="relative"
                    minW="72px"
                    minH="72px"
                    w="72px"
                    h="72px"
                    borderRadius="full"
                    bg="#F2F2F2"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mr={4}
                  >
                    {logo ? (
                      <Image
                        src={logo}
                        alt="Logo"
                        height={"72px"}
                        width={"72px"}
                        borderRadius={"50%"}
                        objectFit={"cover"}
                      />
                    ) : (
                      <Image
                        src={DummyUserImage}
                        height={"72px"}
                        width={"72px"}
                        borderRadius={"50%"}
                        objectFit={"cover"}
                        alt="logo"
                      />
                    )}
                    <Box
                      as="label"
                      position="absolute"
                      bottom="0"
                      right="0"
                      bg="white"
                      borderRadius="full"
                      boxShadow="md"
                      p="5px"
                      cursor="pointer"
                      border="1px solid #E0E0E0"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      htmlFor="logo-upload-input"
                    >
                      {logo ? (
                        <Image
                          src={EditIcon}
                          alt="Upload Logo"
                          height="14px"
                          width="14px"
                        />
                      ) : (
                        <Image
                          src={CameraIcon}
                          alt="Upload Logo"
                          height="14px"
                          width="14px"
                        />
                      )}

                      <Input
                        id="logo-upload-input"
                        type="file"
                        accept="image/png, image/jpeg, image/gif"
                        onChange={handleLogoUpload}
                        display="none"
                      />
                    </Box>
                  </Box>
                  {/* Texts */}
                  <Box className="titleBox">
                    <Text className="workspaceStepLabel">
                      {constant.organizationLogo}
                    </Text>
                    <Text className="workspaceStepDesc">
                      {constant.logoSupportText}
                    </Text>
                  </Box>
                </Box>
                <Box mb={4} className="titleBox">
                  <Text className="workspaceStepLabel">
                    {constant.organization}
                  </Text>
                  <Text className="workspaceStepDesc">
                    {constant.nameOfOrganization}
                  </Text>
                </Box>
                <Box className="workspaceInputbox">
                  <FormLabel className="workspaceInputLabel">
                    {constant.companyName}{" "}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    placeholder={constant.companyName}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    mb={1}
                  />
                  {validationMessages.companyName && (
                    <Text color="red.500" fontSize="sm" mb={4}>
                      {validationMessages.companyName}
                    </Text>
                  )}
                </Box>
                <Box className="workspaceInputbox">
                  <FormLabel className="workspaceInputLabel">
                    {constant.firstName}{" "}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    placeholder={constant.firstName}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    mb={1}
                  />
                  {validationMessages.firstName && (
                    <Text color="red.500" fontSize="sm" mb={4}>
                      {validationMessages.firstName}
                    </Text>
                  )}
                </Box>
                <Box className="workspaceInputbox">
                  <FormLabel className="workspaceInputLabel">
                    {constant.lastName}{" "}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    mb={1}
                  />
                  {validationMessages.lastName && (
                    <Text color="red.500" fontSize="sm" mb={4}>
                      {validationMessages.lastName}
                    </Text>
                  )}
                </Box>
                <Box display={"flex"} justifyContent="end" mt={8}>
                  <Button
                    variant={"brand"}
                    onClick={handleSaveOrganization}
                    isDisabled={!isStep1Valid}
                    isLoading={isLoading}
                    spinner={<Box className="spinner" />}
                  >
                    {constant.saveContinue}
                  </Button>
                </Box>
              </>
            ) : (
              <Box className="workspaceInputbox">
                <FormLabel className="workspaceInputLabel">
                  {constant.enterPassword}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Box position="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={handlePasswordChange}
                    mb={1}
                    pr="40px"
                  />
                  <Box
                    position="absolute"
                    top="50%"
                    right="10px"
                    transform="translateY(-50%)"
                    cursor="pointer"
                    zIndex="1"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Box>
                </Box>
                {passwordValidationMessage && (
                  <Text color="red.500" fontSize="sm" mb={4}>
                    {passwordValidationMessage}
                  </Text>
                )}
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  // Rest of your component remains the same...
  return (
    <Box className="workspaceModal" p={5}>
      {/* Your existing JSX structure */}
      <Box
        display="flex"
        width="100%"
        boxShadow="md"
        borderRadius="12px"
        backgroundColor={"#F8F8F8"}
        overflow="hidden"
      >
        {/* Left Section */}
        <Box width="472px" minW={"472px"} bg="white" p={"28px"}>
          <Text fontSize="20px" fontWeight="600" mb={1}>
            {constant.getStarted}
          </Text>
          <Text fontSize="sm" color="gray.600" mb={7}>
            {constant.completeSteps}{" "}
            <span style={{ fontWeight: "700" }}>{selectedOption}</span>/
            {createWorkspaceOptions.length}
          </Text>
          <Box className="workspaceStepsContainer">
            {createWorkspaceOptions?.map((option, idx) => {
              const isActive = selectedOption === option.id;
              const isCompleted = idx + 1 < selectedOption;

              const isCurrentStepOnlyEnabled = step === "1" || step === "2";
              const isDisabled = isCurrentStepOnlyEnabled
                ? option.id !== 2
                : option.id !== selectedOption;

              return (
                <Box
                  key={option.id}
                  className={
                    "workspaceSteps" +
                    (isActive ? " activeWorkspaceStep" : "") +
                    (isCompleted ? " completedState" : "") +
                    (isDisabled ? " disabledStep" : "")
                  }
                  cursor={isDisabled ? "not-allowed" : "pointer"}
                  onClick={() => {
                    if (!isDisabled) {
                      setSelectedOption(option.id);
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={"22px"}>
                    <Box className="workspaceStepIcon">
                      <Text>{idx + 1}</Text>
                    </Box>
                    <Box>
                      <Text className="workspaceStepLabel">{option.label}</Text>
                      <Text className="workspaceStepDesc">
                        {option.description}
                      </Text>
                    </Box>
                  </Box>
                  <Box className="completedStepIconBox">
                    <Image
                      src={CheckIconCircle}
                      alt="Step Icon"
                      height={"20px"}
                      width={"20px"}
                      className="completedStepIcon"
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          width="70%"
          p={"32px"}
          position="relative"
          className="workspaceModalContentRight"
        >
          <Box mb={6}>
            {selectedOption === 1 && (
              <Box mb={2} className="workspaceProgressText">
                <Text className="workspaceStepLabel">
                  {constant.createOrganization}
                </Text>
                <Text className="workspaceStepDesc">
                  {constant.createOrganizationDesc}
                </Text>
              </Box>
            )}
            {selectedOption === 2 && (
              <Box mb={2} className="workspaceProgressText">
                <Text className="workspaceStepLabel">
                  {constant.signUpText}
                </Text>
                <Text className="workspaceStepDesc">{constant.signUpDesc}</Text>
              </Box>
            )}
            <Box height="8px" bg="gray.200" borderRadius="md" overflow="hidden">
              <Box
                height="100%"
                width={`${
                  (selectedOption / createWorkspaceOptions?.length) * 100
                }%`}
                className="progressBar"
                transition="width 0.3s"
              />
            </Box>
          </Box>
          {renderContent()}
          <Box
            display="flex"
            className="stepsBackBtn"
            justifyContent="center"
            mt={2}
          ></Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkspaceModal;
